import React, { useCallback } from 'react';

import { Col, Row } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { Text } from 'scouting-ui/lib';

import { attendeePanelKey, rsvpCodes } from '@shared/constants';

import Button from '../../Button';
import CollapseDetailsCustom from '../../CollapseDetailsCustom';
import { PersonData, PersonsList } from '../types';
import Person from './Person';
import styles from './PersonList.less';
import PersonSwitch from './PersonSwitch/PersonSwitch';

interface PersonListProps {
  persons: PersonsList;
  rsvpKey?: string;
  onAttendedToggle: (person: PersonData, status: boolean) => void;
  isAttendedToggleLoading: boolean;
  onRemoveAction: (data: PersonData) => void;
  onRSVPAction: (data: PersonData, code: typeof rsvpCodes) => void;
  innerExpandedKeys: string[];
  handleInnerPanelChange: (keys: string[]) => void;
  isRSVP: boolean;
  onOpenModal?: () => void;
  recurringEventId?: number | null;
  isCreateEvent: boolean;
  enableAttendance: boolean;
  hasPackOrDen: boolean;
  myYouthsInEvent?: { [key: string]: string };
  userOrgsMemberIds?: Set<number> | null;
}

const PersonListOriginal: React.FC<PersonListProps> = ({
  persons = {
    youths: [],
    denChiefs: [],
    adults: [],
    guests: [],
  },
  rsvpKey,
  onAttendedToggle = () => {},
  isAttendedToggleLoading = false,
  onRemoveAction = () => {},
  onRSVPAction = () => {},
  innerExpandedKeys,
  handleInnerPanelChange,
  isRSVP,
  onOpenModal,
  recurringEventId,
  isCreateEvent,
  enableAttendance,
  hasPackOrDen = false,
  myYouthsInEvent = {},
  userOrgsMemberIds = null,
}) => {
  const { youths, denChiefs, adults, guests } = persons;
  const showAttendance =
    !recurringEventId && enableAttendance && !isCreateEvent;

  const collapseDetails = useCallback(
    (personsToMap: PersonData[], type: string) => {
      const translationKey = `shared.${type}`;
      const translationTitle = `shared.current.${type}`;

      return (
        <CollapseDetailsCustom
          header={
            <Text>
              <FormattedMessage id={translationTitle} /> ({personsToMap.length})
            </Text>
          }
          onChange={handleInnerPanelChange}
          expandedKeys={innerExpandedKeys}
          panelKeyNew={
            isRSVP ? `${rsvpKey}.${translationKey}` : `${translationKey}`
          }
        >
          {personsToMap.length > 0 && showAttendance && (
            <Row align="middle" justify="end">
              <Col span={24} className={styles.attended}>
                <Text>
                  <FormattedMessage id="shared.switch.header" />
                </Text>
              </Col>
            </Row>
          )}

          {personsToMap.map((person: PersonData, index: number) => {
            const { userId, memberId } = person;

            return (
              <Row key={`${index}-${userId}`} align="middle">
                <Col span={showAttendance ? 18 : 24}>
                  <Person
                    key={userId}
                    data={person}
                    rsvpKey={rsvpKey}
                    onRemoveAction={onRemoveAction}
                    onRSVPAction={onRSVPAction}
                    isRSVP={isRSVP}
                    isMyYouth={!!myYouthsInEvent?.[`${userId}`]}
                    canRemove={
                      userOrgsMemberIds ? userOrgsMemberIds.has(memberId) : true
                    }
                  />
                </Col>
                {showAttendance && (
                  <Col span={6} className={styles.attended}>
                    <PersonSwitch
                      key={userId}
                      person={person}
                      onAttendedToggle={onAttendedToggle}
                      isAttendedToggleLoading={isAttendedToggleLoading}
                      disabled={
                        userOrgsMemberIds
                          ? !userOrgsMemberIds.has(memberId)
                          : false
                      }
                    />
                  </Col>
                )}
              </Row>
            );
          })}
          {!isRSVP && translationKey === 'shared.guests' && (
            <Row justify="center">
              <Button
                ghost
                noBorder
                color="info"
                size="small"
                uppercase={false}
                block
                style={{
                  textAlign: 'center',
                  textDecoration: 'underline blue',
                }}
                onClick={onOpenModal}
                disabled={!!recurringEventId}
              >
                <FormattedMessage id="shared.guests.addButton" />
              </Button>
            </Row>
          )}
        </CollapseDetailsCustom>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onRemoveAction,
      rsvpKey,
      onAttendedToggle,
      isAttendedToggleLoading,
      onRSVPAction,
      innerExpandedKeys,
      handleInnerPanelChange,
      isRSVP,
      onOpenModal,
    ],
  );

  return (
    <React.Fragment>
      {collapseDetails(youths, attendeePanelKey.YOUTHS)}
      {hasPackOrDen && collapseDetails(denChiefs, attendeePanelKey.DEN_CHIEFS)}
      {collapseDetails(adults, attendeePanelKey.ADULTS)}
      {collapseDetails(guests, attendeePanelKey.GUESTS)}
    </React.Fragment>
  );
};

const PersonList = React.memo(PersonListOriginal);

export default PersonList;
